<template>
  <v-container fluid class="px-8">
    <v-card>
      <v-card-text class="pb-0">
        <v-container fluid>
          <v-row>
            <v-col>
              <v-text-field
                v-model="company.CompanyName"
                outlined
                :label="'Company Name'"
                :placeholder="$t(`form.company.placeholders.name`)"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col>
              <core-list-edit
                :key="1"
                v-model="contacts"
                color="grey lighten-4"
                icon="mdi-account"
                :title="$t('common.contacts')"
                :max-height="500"
                rights-group="address"
                use-drag-handle
                separator
                allow-move
                allow-add
                :allow-remove="index => index == null || !contacts[index].IsPortalUser"
                before-remove
                @add-list-item="createContact"
                @before-remove="deleteContact"
                @form-error="isContactFieldMissing=true;"
              >
                <template v-slot:list-item="{ index }">
                  <v-tooltip v-if="contacts[index].IsPortalUser" top>
                    <template v-slot:activator="{ on }">
                      <v-icon v-on="on">
                        mdi-account-box
                      </v-icon>
                    </template>
                    <span>this contact is also a user</span>
                  </v-tooltip>
                  <span v-else style="width: 20px;" />
                  <v-list-item-content class="pa-4">
                    <form-contact
                      :ref="`form-contact-${index}`"
                      :contact="contacts[index]"
                    />
                  </v-list-item-content>
                </template>
              </core-list-edit>
            </v-col>
          </v-row>

          <v-row>
            <v-col :cols="isCustomer ? 6 : 12">
              <core-list-edit
                :key="2"
                v-model="billingAddresses"
                color="grey lighten-4"
                icon="mdi-map-marker"
                :title="$t('common.billingAddresses')"
                :max-height="500"
                rights-group="address"
                use-drag-handle
                separator
                allow-move
                allow-remove
                show-empty
              >
                <template v-slot:list-item="{ index }">
                  <v-list-item-content class="pa-4">
                    <form-address
                      v-model="billingAddresses[index]"
                      address-type="business"
                    />
                  </v-list-item-content>
                </template>
              </core-list-edit>
            </v-col>

            <v-col v-if="isCustomer" cols="6">
              <core-list-edit
                :key="1"
                v-model="shippingAddresses"
                color="grey lighten-4"
                :max-height="500"
                rights-group="address"
                :hide-data="shippingSameAsBilling"
                use-drag-handle
                separator
                allow-move
                :allow-add="!shippingSameAsBilling"
                allow-remove
                show-empty
                before-remove
                @add-list-item="addShippingAddress"
                @before-remove="removeShippingAddress"
              >
                <template #title>
                  <v-icon class="mr-2">
                    mdi-map-marker
                  </v-icon>

                  {{ $t('common.shippingAddresses') }}

                  <v-switch
                    v-model="shippingSameAsBilling"
                    class="ml-4 pa-0 ma-0"
                    :label="'Same as billing'"
                    hide-details
                  />
                </template>

                <template v-slot:list-item="{ index }">
                  <v-list-item-content class="pa-4">
                    <form-address
                      v-model="shippingAddresses[index]"
                      address-type="business"
                    />
                  </v-list-item-content>
                </template>
              </core-list-edit>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn
          :disabled="isSaveDisabled || loading"
          color="primary"
          depressed
          :loading="loading"
          @click="save"
        >
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-container>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';
import { models } from 'feathers-vuex';
import UpdateEntityMixin from '../../mixins/update-entity';
import AuthMixin from '../../mixins/auth';
import { LEVEL } from '../../utils/log';

const { Contact } = models.api;

export default {
  name: 'CompanySettings',

  mixins: [
    UpdateEntityMixin,
    AuthMixin,
  ],

  data() {
    return {
      billingAddresses: [],

      shippingAddresses: [],
      shippingAddressesToRemove: [],

      shippingSameAsBilling: false,
      isContactFieldMissing: false,

      originalContacts: [],
      contactsToRemove: [],
      newContacts: [],
      contactsToUpdate: [],
      loading: false,

      originalData: {
        contacts: [],
        company: {},
        shippingAddresses: [],
      },

      rules: {
        friendlyUrl(value) {
          if (/^(https?|chrome):\/\/[^\s$.?#].[^\s]*$/.test(value)) {
            return true;
          }
          return 'invalid';
          // return this.$t('invalid.url');
        }
      },
    };
  },

  computed: {
    ...mapGetters('company', {
      findCompanies: 'find',
      getCompany: 'get',
    }),

    ...mapGetters('contacts', {
      findContacts: 'find',
    }),
    ...mapGetters('users', {
      findUsers: 'find',
    }),

    ...mapGetters('countries', {
      findCountries: 'find',
    }),

    ...mapGetters('regions', {
      findRegions: 'find',
    }),

    ...mapGetters('shipping-addresses', {
      findShippingAddresses: 'find',
    }),

    isCustomer() {
      return this.company.CompanyType === 'Customer';
    },

    fetchedCountries() {
      return _.get(this.findCountries({ query: {} }), 'data', []);
    },

    fetchedRegions() {
      return _.get(this.findRegions({ query: {} }), 'data', []);
    },

    company() {
      return _.get(this.findCompanies({ query: {} }), 'data.0', {});
    },

    fetchedContacts() {
      return this.findContacts({ query: {} }).data;
    },

    /* TODO: fetchedContacts updates, but this doesn't recompute??? */
    contacts() {
      // TODO we will delete the temp contact when we click delete
      const deletedIds = this.contactsToRemove.map(ct => ct._id);
      return Contact.findInStore({ temps: true,
        query: {
          _id: {
            $nin: deletedIds
          }
        } }).data;
    },

    fetchedShippingAddresses() {
      return this.findShippingAddresses({ query: {} }).data;
    },

    // TODO: Doesn't work on first load? After 1 save it works
    isSaveDisabled() {
      return false;
    },
  },

  watch: {
    fetchedContacts: {
      deep: true,
      /* Temp hack to force contacts computed prop to refresh. It seems as though it recomputes
      when we change newContacts, so why doesn't it recompute when 'patch' updates fetchedContacts? */
      handler() {
        this.newContacts.push();
        this.newContacts.pop();
      }
    }
  },

  mounted() {
    const fetches = [
      async () => {
        Contact.find({ query: {} });
        this.fetchUsers({ query: {} });
        this.originalData.contacts = _.cloneDeep(this.fetchedContacts);
        // this.contacts = _.cloneDeep(this.fetchedContacts);
      },
      async () => {
        await this.fetchCompanies({ query: {} });
        this.originalData.company = _.cloneDeep(this.company);

        const billing = this.convertRemoteAddress(this.company);

        this.$set(this.billingAddresses, 0, billing);
      },
      async () => {
        await this.fetchShippingAddresses({ query: {} });
        const converted = this.fetchedShippingAddresses.map(sa => this.convertRemoteAddress(sa));
        this.originalData.shippingAddresses = _.cloneDeep(converted);
        this.shippingAddresses = _.cloneDeep(converted);
      },
    ];
    fetches.forEach(f => f());
  },

  methods: {
    ...mapActions('app', [
      'pushSnack'
    ]),
    ...mapActions('company', {
      fetchCompanies: 'find',
    }),

    ...mapActions('contacts', {
      fetchContacts: 'find',
    }),
    ...mapActions('users', {
      fetchUsers: 'find',
    }),
    ...mapActions('shipping-addresses', {
      fetchShippingAddresses: 'find',
    }),

    ...mapMutations('contacts', {
      addContactInStore: 'addItem',
      removeContactFromStore: 'removeItem',
    }),

    addShippingAddress() {
      const sa = new this.$FeathersVuex.api.ShippingAddress({});
      this.shippingAddresses.push(sa);
    },

    removeShippingAddress(sa) {
      const index = _.findIndex(this.shippingAddresses, sa);
      if (index !== -1) {
        this.shippingAddresses.splice(index, 1);
        this.shippingAddressesToRemove.push(sa);
      }
    },

    /* TODO: Handle field remapping in hooks or servi */
    convertLocalAddress(toRemote, from) {
      const fieldsToMap = [
        ['name', 'Addr1'],
        ['address1', 'Addr2'],
        ['address2', 'Addr3'],
        ['city', 'City'],
        ['zip', 'PostCodeZip'],
        ['country', 'Country'],
        ['region', 'ProvState'],
      ];
      fieldsToMap.forEach(f => {
        this.$set(
          toRemote,
          f[1],
          from[f[0]] === 'country' ? _.toUpper(from.country) : from[f[0]],
        );
        // delete toRemote[f[0]];
      });
      return toRemote;
    },

    convertRemoteAddress(fromRemote) {
      const fieldsToMap = [
        ['name', 'Addr1'],
        ['address1', 'Addr2'],
        ['address2', 'Addr3'],
        ['city', 'City'],
        ['zip', 'PostCodeZip'],
        ['country', 'Country'],
        ['region', 'ProvState'],
      ];
      fieldsToMap.forEach(f => {
        this.$set(
          fromRemote,
          f[0],
          fromRemote[f[1]] === 'Country' ? _.startCase(_.toLower(fromRemote.Country)) : fromRemote[f[1]]
        );
        // delete fromRemote[f[1]];
      });
      return fromRemote;
    },

    deleteContact(contact) {
      // eslint-disable-next-line no-underscore-dangle
      if (contact.__isTemp) {
        contact.remove();
      } else {
        this.contactsToRemove.push(contact);
      }
    },
    async createContact() {
      // eslint-disable-next-line no-unused-vars
      const contact = new Contact();
    },


    async save() {
      this.loading = true;
      await Promise.all(this.contacts.map(contact => contact.save()));
      // [].concat(this.contactsToRemove) to be able to modify
      // this.contactsToRemove in map function

      // eslint-disable-next-line array-callback-return
      await Promise.all([].concat(this.contactsToRemove).map(contact => {
        contact.remove();
        // remove contact in this.contactsToRemove
        _.remove(this.contactsToRemove, c => _.isEqual(c, contact));
      }));
      this.loading = false;
    },
  }
};
</script>

<style scoped>
</style>
